import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from "react";
import {
  tokens,
  Typography,
  Modal,
  Select,
  Button,
  SvgIcon,
  SvgNames,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { ReactComponent as UploadIcon } from "../assets/images/upload-icon.svg";
import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { Card, CardHeaderDivider } from "components/atoms/Card";
import {
  FileUploadError,
  UploadAttachments,
  handleFileSelected,
} from "components/molecules/UploadAttachments";
import { Attachment } from "utils/usageUtils";
import { UploadErrorModal } from "components/molecules/UploadErrorModal";
import { UsageFormContext } from "providers/UsageFormContext";
import { AttachmentActionButton } from "components/atoms/AttachmentActionButton";
import {
  ImmediateUploadConfig,
  useImmediateAttachmentUpload,
} from "hooks/useImmediateAttachmentUpload";

const DOCUMENT_TYPES = [
  "CPUC",
  "Customer Agreement",
  "Home Owner Insurance",
  "IPA Rebate",
  "Load Calculator",
  "NEM Agreement",
  "LLC Certificate",
  "Proof of Title",
  "Proof of Usage",
  "Trust Docs",
  "Utility Bill",
  "Other",
];

type DocumentUploadWithModalProps = {
  isMobile?: boolean;
  uploadedFiles: Attachment[] | SplatCheckoutDocument[];
  uploadedNewFiles?: Attachment[];
  setUploadedNewFiles?: Dispatch<SetStateAction<Attachment[]>>;
  immediateUpload?: ImmediateUploadConfig;
  documentType: string;
  description: string;
  title: string;
  documentLoading?: boolean;
  bannerContent?: React.ReactNode;
};

const DocumentUploadWithModal = ({
  isMobile,
  uploadedFiles,
  uploadedNewFiles = [],
  setUploadedNewFiles,
  immediateUpload,
  documentType,
  description,
  title,
  documentLoading,
  bannerContent,
}: DocumentUploadWithModalProps) => {
  const [errors, setErrors] = useState<FileUploadError[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { setIsDirty } = useContext(UsageFormContext);
  const {
    pendingAttachments: immediatePendingAttachments,
    setPendingAttachments: setImmediatePendingAttachments,
    isUploading,
  } = useImmediateAttachmentUpload(immediateUpload);

  const pendingAttachments = immediateUpload
    ? immediatePendingAttachments
    : uploadedNewFiles;
  const setPendingAttachments = immediateUpload
    ? setImmediatePendingAttachments
    : setUploadedNewFiles!;
  const isLoading = documentLoading || isUploading;
  const handleAddClick = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setSelectedFile(null);
    setShowModal(false);
    setSelectedDocumentType("");
  };

  const handleDocumentTypeSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedDocumentType(event.target.value);
  };

  const handleModalConfirm = () => {
    if (selectedFile) {
      // Create a FileList-like object from the single File
      const fileList = {
        // Override the name of the file to be the selected document type
        // but keep the original extension (e.g. .pdf, .jpg, etc.)
        0: Object.defineProperty(selectedFile, "name", {
          value: `${selectedDocumentType}${selectedFile.name.substring(
            selectedFile.name.lastIndexOf(".")
          )}`,
        }),
        length: 1,
        item: (index: number) => (index === 0 ? selectedFile : null),
        [Symbol.iterator]: function* () {
          yield selectedFile;
        },
      } as FileList;

      handleFileSelected(fileList, {
        documentType: "Other",
        onError: (errors) => setErrors(errors),
        onUploadedNewFiles: setPendingAttachments,
        setIsDirty,
      });
    }
    handleModalClose();
  };

  return (
    <Card>
      <header data-testid="document-upload-header">
        <Typography size={tokens.FONT_SIZE_3}>{title}</Typography>
      </header>
      <CardHeaderDivider />
      {bannerContent}
      <div className="document-upload-body">
        <UploadAttachments
          setIsDirty={setIsDirty}
          uploadedFiles={uploadedFiles}
          documentType={documentType}
          buttonSize="md"
          uploadedNewFiles={pendingAttachments}
          onUploadedNewFiles={setPendingAttachments}
          onError={(errors) => setErrors(errors)}
          description={description}
          allowDownload
          allowPreview
          documentLoading={isLoading}
          buttonText="Add"
          buttonIcon={<SvgIcon name={SvgNames.Add} />}
          buttonColor="secondary"
          onButtonClick={handleAddClick}
          loadingColor="black"
        />
      </div>
      <UploadErrorModal errors={errors} onClose={() => setErrors([])} />
      {showModal && (
        <Modal
          testId="document-upload-modal"
          title="Document requirements"
          type="info"
          customModalWidth={840}
          primaryButton={{
            text: "Continue",
            onClick: handleModalConfirm,
            disabled: !selectedDocumentType || !selectedFile,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: handleModalClose,
          }}
          onClose={handleModalClose}
        >
          <ModalContent>
            <Typography>
              Please select the type of document you wish to upload:
            </Typography>
            <Select
              value={selectedDocumentType}
              onChange={handleDocumentTypeSelect}
              options={[
                { label: "Select document type", value: "" },
                ...DOCUMENT_TYPES.map((type) => ({
                  label: type,
                  value: type,
                })),
              ]}
            />
            {selectedDocumentType && (
              <FileUploadInputContainer isMobile={isMobile}>
                <Typography color={tokens.TINTS_OFF_WHITE_10}>
                  Upload {selectedDocumentType} for review
                </Typography>
                {selectedFile ? (
                  <AttachmentActionButton
                    file={{
                      documentName: selectedFile.name,
                      documentType: "Other",
                      contentType: selectedFile.type,
                      url: URL.createObjectURL(selectedFile),
                      documentBlob: selectedFile,
                    }}
                    allowDownload={false}
                    allowPreview
                    allowDelete
                    deleteFile={() => setSelectedFile(null)}
                  />
                ) : (
                  <StyledButton
                    isMobile={isMobile}
                    size="md"
                    color="primary"
                    iconRight={<UploadIcon />}
                    onClick={() =>
                      document.getElementById("file-upload-input")?.click()
                    }
                  >
                    Upload
                  </StyledButton>
                )}
                <FileUploadInput
                  type="file"
                  id="file-upload-input"
                  data-testid="file-upload-input"
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                    }
                  }}
                />
              </FileUploadInputContainer>
            )}
          </ModalContent>
        </Modal>
      )}
    </Card>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

const StyledButton = styled(Button)<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const FileUploadInputContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;

  ${({ isMobile }) =>
    isMobile &&
    `
    flex-direction: column;
    align-items: flex-start;
  `}
`;

const FileUploadInput = styled.input`
  display: none;
`;

export { DocumentUploadWithModal };
